/**
 * Network Panel Component
 *
 * Embeddable network infrastructure panel for use in Infrastructure page.
 * Shows network resources linked to GPU instances/containers with automatic
 * cleanup status and pipeline visualization.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useProgress } from '../contexts/ProgressContext';
import {
  Network,
  Globe,
  Shield,
  Layers,
  RefreshCw,
  Loader2,
  Trash2,
  Plus,
  ChevronRight,
  Server,
  ArrowRight,
  Clock,
  AlertTriangle,
  Activity,
  Container,
  Link2,
  Unlink,
  CheckCircle2,
} from 'lucide-react';
import {
  detailedNetworkApi,
  SecurityGroupRule,
  AddSecurityGroupRuleRequest,
  gpuJobsApi,
  ecsApi,
} from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

// Types
interface NetworkPanelProps {
  mode: 'instances' | 'containers';
  onRefresh?: () => void;
}

interface GpuJob {
  id: string;
  instanceId?: string;
  instanceType: string;
  status: string;
  vpcId?: string;
  securityGroupId?: string;
  publicIpAddress?: string;
  privateIpAddress?: string;
  completedAt?: string;
}

// Animation variants
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

export default function NetworkPanel({ mode, onRefresh }: NetworkPanelProps) {
  const queryClient = useQueryClient();
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const [networkToDestroy, setNetworkToDestroy] = useState<{ jobId: string; instanceId: string; vpcId: string } | null>(null);
  const [addRuleModal, setAddRuleModal] = useState<{ groupId: string; direction: 'inbound' | 'outbound' } | null>(null);
  const [newRule, setNewRule] = useState({
    protocol: 'tcp',
    fromPort: '',
    toPort: '',
    cidrIp: '0.0.0.0/0',
    description: '',
  });
  const [networkPage, setNetworkPage] = useState(1);
  const networkPerPage = 5;
  const [selectedNetworks, setSelectedNetworks] = useState<Set<string>>(new Set());
  const [showPipelineView, setShowPipelineView] = useState(true);

  const { startProgress, completeProgress } = useProgress();

  // Fetch GPU jobs
  const { data: gpuJobs = [] } = useQuery({
    queryKey: ['gpu-jobs'],
    queryFn: gpuJobsApi.list,
    refetchInterval: 10000,
    enabled: mode === 'instances',
  });

  // Fetch ECS clusters for container mode
  const { data: ecsClusters } = useQuery({
    queryKey: ['all-ecs-clusters'],
    queryFn: ecsApi.listClusters,
    refetchInterval: 15000,
    enabled: mode === 'containers',
  });

  const hasRecentCancellations = gpuJobs.some((job: GpuJob) => {
    if (job.status !== 'cancelled' && job.status !== 'terminated') return false;
    if (!job.completedAt) return false;
    const completedTime = new Date(job.completedAt).getTime();
    const now = Date.now();
    return now - completedTime < 30000;
  });

  const shouldRefreshFast = hasRecentCancellations;

  // Fetch network data
  const { data: networkData, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['detailed-network-status'],
    queryFn: detailedNetworkApi.getDetailedStatus,
    refetchInterval: shouldRefreshFast ? 3000 : 15000,
    staleTime: shouldRefreshFast ? 1000 : 10000,
  });

  // Add security group rule mutation
  const addRuleMutation = useMutation({
    mutationFn: ({ groupId, rule }: { groupId: string; rule: AddSecurityGroupRuleRequest }) =>
      detailedNetworkApi.addSecurityGroupRule(groupId, rule),
    onSuccess: () => {
      toast.success('Security group rule added successfully!');
      setAddRuleModal(null);
      setNewRule({ protocol: 'tcp', fromPort: '', toPort: '', cidrIp: '0.0.0.0/0', description: '' });
      refetch();
    },
    onError: (error: Error) => {
      toast.error(`Failed to add rule: ${error.message}`);
    },
  });

  // Remove security group rule mutation
  const removeRuleMutation = useMutation({
    mutationFn: ({ groupId, rule }: { groupId: string; rule: any }) =>
      detailedNetworkApi.removeSecurityGroupRule(groupId, rule),
    onSuccess: () => {
      toast.success('Security group rule removed successfully!');
      refetch();
    },
    onError: (error: Error) => {
      toast.error(`Failed to remove rule: ${error.message}`);
    },
  });

  // Remove network mutation - with AWS-validated progress tracking
  const removeNetworkMutation = useMutation({
    mutationFn: async ({ jobId, instanceId, vpcId }: { jobId: string; instanceId?: string; vpcId?: string }) => {
      // Get job details for AWS-validated progress tracking
      const job = gpuJobs.find(j => j.id === jobId);
      
      // Start progress with initial resources for AWS validation
      // The polling will continue even if the API call times out
      startProgress(jobId, {
        vpcId: vpcId || job?.vpcId,
        securityGroupId: job?.securityGroupId,
        instanceId: instanceId || job?.instanceId,
      });
      
      try {
        return await gpuJobsApi.remove(jobId);
      } catch (error: any) {
        // If timeout, don't fail - let the AWS polling continue
        if (error.message?.includes('timeout')) {
          toast(
            <div>
              <p className="font-medium">Removal in progress</p>
              <p className="text-sm opacity-80 mt-1">Backend timed out, tracking via AWS polling</p>
            </div>,
            { duration: 4000, icon: '⏳' }
          );
          return { 
            deleted: true, 
            message: 'Removal initiated - polling AWS for completion' 
          };
        }
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      const { jobId } = variables;
      
      // If this was a timeout response, progress polling will continue
      if (data.message?.includes('polling AWS')) {
        setNetworkToDestroy(null);
        queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
        if (onRefresh) onRefresh();
        return;
      }
      
      const networkCleaned = data.message.includes('network');

      completeProgress(
        jobId,
        true,
        networkCleaned
          ? 'All network resources have been cleaned up'
          : 'Instance removed, network preserved',
        !networkCleaned
      );

      if (networkCleaned) {
        toast.success(
          <div>
            <p className="font-medium">Network destroyed successfully</p>
            <p className="text-sm opacity-80 mt-1">All associated resources have been cleaned up</p>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.success('Instance removed successfully (network preserved)');
      }
      setNetworkToDestroy(null);
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
      queryClient.invalidateQueries({ queryKey: ['network-status'] });
      if (onRefresh) onRefresh();
    },
    onError: (error: Error, variables) => {
      const { jobId } = variables;
      
      // Don't complete progress on timeout - let AWS polling continue
      if (!error.message?.includes('timeout')) {
        completeProgress(jobId, false, `Failed: ${error.message}`);
      }
      
      toast.error(
        <div>
          <p className="font-medium">{error.message?.includes('timeout') ? 'API timeout' : 'Failed to destroy network'}</p>
          <p className="text-sm opacity-80 mt-1">{error.message}</p>
          {error.message?.includes('timeout') && (
            <p className="text-xs opacity-70 mt-2">Still tracking progress via AWS polling...</p>
          )}
        </div>,
        { duration: 6000 }
      );
      setNetworkToDestroy(null);
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      if (onRefresh) onRefresh();
    },
  });

  const handleAddRule = () => {
    if (!addRuleModal) return;

    const port = parseInt(newRule.fromPort);
    if (isNaN(port) || port < 0 || port > 65535) {
      toast.error('Invalid port number');
      return;
    }

    addRuleMutation.mutate({
      groupId: addRuleModal.groupId,
      rule: {
        direction: addRuleModal.direction,
        protocol: newRule.protocol,
        fromPort: parseInt(newRule.fromPort),
        toPort: parseInt(newRule.toPort) || parseInt(newRule.fromPort),
        cidrIp: newRule.cidrIp,
        description: newRule.description || undefined,
      },
    });
  };

  const handleRemoveRule = (groupId: string, direction: 'inbound' | 'outbound', rule: SecurityGroupRule) => {
    if (!confirm('Are you sure you want to remove this rule?')) return;

    const firstCidr = rule.ipRanges[0]?.cidr;
    if (!firstCidr) {
      toast.error('Cannot remove rule without CIDR');
      return;
    }

    removeRuleMutation.mutate({
      groupId,
      rule: {
        direction,
        protocol: rule.protocol,
        fromPort: rule.fromPort || 0,
        toPort: rule.toPort || 0,
        cidrIp: firstCidr,
      },
    });
  };

  const getProtocolLabel = (protocol: string) => {
    switch (protocol) {
      case 'tcp': return 'TCP';
      case 'udp': return 'UDP';
      case 'icmp': return 'ICMP';
      case 'all': return 'All Traffic';
      case '-1': return 'All Traffic';
      default: return protocol.toUpperCase();
    }
  };

  const getPortDisplay = (rule: SecurityGroupRule) => {
    if (rule.protocol === 'all' || rule.protocol === '-1') return 'All';
    if (rule.protocol === 'icmp') return 'N/A';
    if (rule.fromPort === rule.toPort) return String(rule.fromPort);
    return `${rule.fromPort}-${rule.toPort}`;
  };

  const hasAnyInfrastructure = networkData && (
    networkData.vpcs.length > 0 ||
    networkData.subnets.length > 0 ||
    networkData.securityGroups.length > 0 ||
    networkData.internetGateways.length > 0 ||
    networkData.routeTables.length > 0 ||
    networkData.iamRoles.length > 0
  );

  return (
    <motion.div variants={itemVariants} initial="hidden" animate="visible" className="space-y-4">
      {/* Pipeline Indicator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPipelineView(!showPipelineView)}
            className={clsx(
              'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
              showPipelineView
                ? 'bg-gpu-500/20 text-gpu-400 border border-gpu-500/30'
                : 'bg-surface-800 text-surface-400 border border-surface-700 hover:text-surface-300'
            )}
          >
            <Link2 className="w-4 h-4" />
            Pipeline View
          </button>
          <span className="text-xs text-surface-500">
            {mode === 'instances' ? 'Instance → Network' : 'Container → Network'} relationship
          </span>
        </div>
        <div className="flex items-center gap-2">
          {networkData?.lastUpdated && (
            <span className="text-xs text-surface-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Updated {formatDistanceToNow(new Date(networkData.lastUpdated), { addSuffix: true })}
            </span>
          )}
          <button
            onClick={() => {
              refetch();
              if (onRefresh) onRefresh();
            }}
            disabled={isLoading || isRefetching}
            className="btn-ghost text-xs"
          >
            <RefreshCw className={clsx('w-3 h-3', (isLoading || isRefetching) && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Auto-Cleanup Notice */}
      <div className="p-3 rounded-lg bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-emerald-300 mb-1">Automatic Network Cleanup Enabled</h4>
            <p className="text-xs text-surface-400">
              When you terminate {mode === 'instances' ? 'GPU instances' : 'containers'}, associated network resources
              (VPC, subnets, security groups) are <span className="text-emerald-400 font-medium">automatically cleaned up</span> if
              no other resources are using them. This keeps your AWS account clean and avoids unnecessary costs.
            </p>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && !networkData && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gpu-400" />
        </div>
      )}

      {/* Pipeline View */}
      {showPipelineView && hasAnyInfrastructure && networkData && (
        <div className="card bg-gradient-to-br from-surface-900 to-surface-800">
          <h4 className="text-sm font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4 text-gpu-400" />
            Resource Pipeline
          </h4>

          {mode === 'instances' && gpuJobs.length > 0 ? (
            <div className="space-y-3">
              {(() => {
                // Group jobs by VPC
                const jobsByVpc = gpuJobs.reduce((acc: Record<string, GpuJob[]>, job: GpuJob) => {
                  const vpcId = job.vpcId || 'no-vpc';
                  if (!acc[vpcId]) acc[vpcId] = [];
                  acc[vpcId].push(job);
                  return acc;
                }, {} as Record<string, GpuJob[]>);

                const vpcEntries = Object.entries(jobsByVpc);
                const startIndex = (networkPage - 1) * networkPerPage;
                const paginatedEntries = vpcEntries.slice(startIndex, startIndex + networkPerPage);

                return paginatedEntries.map(([vpcId, jobs]) => {
                  const vpc = networkData.vpcs.find(v => v.vpcId === vpcId);
                  const subnets = networkData.subnets.filter(s => s.vpcId === vpcId);
                  const sgs = networkData.securityGroups.filter(sg => sg.vpcId === vpcId);
                  const isShared = jobs.length > 1;

                  return (
                    <div
                      key={vpcId}
                      className="p-3 rounded-lg bg-surface-800/50 border border-surface-700/50 hover:border-surface-600/50 transition-colors"
                    >
                      {/* Pipeline Flow Visualization */}
                      <div className="flex items-center gap-2 mb-3">
                        {/* Instances */}
                        <div className="flex-1">
                          <div className="flex flex-wrap gap-1">
                            {jobs.map((job: GpuJob) => (
                              <div
                                key={job.id}
                                className={clsx(
                                  'flex items-center gap-1.5 px-2 py-1 rounded text-xs',
                                  job.status === 'running' ? 'bg-emerald-500/20 text-emerald-300' :
                                  job.status === 'stopped' ? 'bg-orange-500/20 text-orange-300' :
                                  job.status === 'terminated' || job.status === 'cancelled' ? 'bg-red-500/20 text-red-300' :
                                  'bg-surface-700/50 text-surface-300'
                                )}
                              >
                                <Server className="w-3 h-3" />
                                {job.instanceId?.slice(-8) || job.id.slice(0, 8)}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Arrow */}
                        <div className="flex items-center px-2">
                          <ArrowRight className="w-4 h-4 text-gpu-400" />
                        </div>

                        {/* Network Resources */}
                        <div className="flex-1">
                          <div className="flex flex-wrap gap-1">
                            {vpc && (
                              <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-blue-500/20 text-blue-300 text-xs">
                                <Globe className="w-3 h-3" />
                                VPC
                              </div>
                            )}
                            {subnets.length > 0 && (
                              <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-teal-500/20 text-teal-300 text-xs">
                                <Layers className="w-3 h-3" />
                                {subnets.length} Subnet{subnets.length > 1 ? 's' : ''}
                              </div>
                            )}
                            {sgs.length > 0 && (
                              <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-orange-500/20 text-orange-300 text-xs">
                                <Shield className="w-3 h-3" />
                                {sgs.length} SG{sgs.length > 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Cleanup Status */}
                        <div className="flex items-center gap-2">
                          {isShared ? (
                            <span className="flex items-center gap-1 px-2 py-1 rounded bg-blue-500/10 text-blue-400 text-xs">
                              <Link2 className="w-3 h-3" />
                              Shared
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 text-xs">
                              <Unlink className="w-3 h-3" />
                              Auto-cleanup
                            </span>
                          )}
                        </div>
                      </div>

                      {/* VPC Details */}
                      {vpc && (
                        <div className="grid grid-cols-4 gap-2 text-xs border-t border-surface-700/30 pt-2">
                          <div>
                            <span className="text-surface-500">VPC ID</span>
                            <p className="font-mono text-surface-300">{vpcId.slice(0, 16)}...</p>
                          </div>
                          <div>
                            <span className="text-surface-500">CIDR</span>
                            <p className="font-mono text-surface-300">{vpc.cidrBlock}</p>
                          </div>
                          <div>
                            <span className="text-surface-500">State</span>
                            <p className={vpc.state === 'available' ? 'text-emerald-400' : 'text-surface-400'}>
                              {vpc.state}
                            </p>
                          </div>
                          <div>
                            <span className="text-surface-500">Instances</span>
                            <p className="text-surface-300">{jobs.length} linked</p>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      {jobs.some((j: GpuJob) => ['stopped', 'terminated', 'cancelled', 'completed', 'failed'].includes(j.status)) && (
                        <div className="flex justify-end mt-2 pt-2 border-t border-surface-700/30">
                          {jobs.map((job: GpuJob) => (
                            ['stopped', 'terminated', 'cancelled', 'completed', 'failed'].includes(job.status) && (
                              <button
                                key={job.id}
                                onClick={() => setNetworkToDestroy({
                                  jobId: job.id,
                                  instanceId: job.instanceId || job.id,
                                  vpcId: vpcId,
                                })}
                                disabled={removeNetworkMutation.isPending}
                                className="flex items-center gap-1.5 px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded transition-colors"
                              >
                                <Trash2 className="w-3 h-3" />
                                Remove {job.instanceId?.slice(-6) || job.id.slice(0, 6)}
                              </button>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          ) : mode === 'containers' && ecsClusters?.clusters && ecsClusters.clusters.length > 0 ? (
            <div className="space-y-3">
              {ecsClusters.clusters.map((cluster: any) => (
                <div
                  key={cluster.clusterArn}
                  className="p-3 rounded-lg bg-surface-800/50 border border-surface-700/50 hover:border-surface-600/50 transition-colors"
                >
                  {/* Pipeline Flow */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-gpu-500/20 text-gpu-300 text-xs">
                      <Container className="w-3 h-3" />
                      {cluster.clusterName}
                    </div>
                    <ArrowRight className="w-4 h-4 text-gpu-400" />
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-blue-500/20 text-blue-300 text-xs">
                      <Network className="w-3 h-3" />
                      awsvpc Network
                    </div>
                    <span className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 text-xs ml-auto">
                      <Unlink className="w-3 h-3" />
                      Auto-cleanup on delete
                    </span>
                  </div>

                  {/* Cluster Details */}
                  <div className="grid grid-cols-4 gap-2 text-xs border-t border-surface-700/30 pt-2">
                    <div>
                      <span className="text-surface-500">Status</span>
                      <p className={cluster.status === 'ACTIVE' ? 'text-emerald-400' : 'text-surface-400'}>
                        {cluster.status}
                      </p>
                    </div>
                    <div>
                      <span className="text-surface-500">Instances</span>
                      <p className="text-surface-300">{cluster.registeredContainerInstancesCount}</p>
                    </div>
                    <div>
                      <span className="text-surface-500">Running Tasks</span>
                      <p className="text-surface-300">{cluster.runningTasksCount}</p>
                    </div>
                    <div>
                      <span className="text-surface-500">Pending Tasks</span>
                      <p className="text-surface-300">{cluster.pendingTasksCount}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-surface-400">
              <Network className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No {mode === 'instances' ? 'GPU instances' : 'containers'} deployed</p>
              <p className="text-sm text-surface-500 mt-1">
                Create {mode === 'instances' ? 'GPU instances' : 'containers'} to view their network infrastructure
              </p>
            </div>
          )}
        </div>
      )}

      {/* Detailed Network View - Instance Mode */}
      {mode === 'instances' && hasAnyInfrastructure && networkData && gpuJobs.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Network className="w-5 h-5 text-gpu-400" />
              <h3 className="text-lg font-semibold text-surface-100">Network Details</h3>
            </div>

            {/* Bulk Actions */}
            {selectedNetworks.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-surface-400">{selectedNetworks.size} selected</span>
                <button
                  onClick={() => {
                    const destroyableJobs = gpuJobs.filter((job: GpuJob) =>
                      selectedNetworks.has(job.id) &&
                      ['stopped', 'terminated', 'cancelled'].includes(job.status)
                    );

                    if (destroyableJobs.length === 0) {
                      toast.error('No destroyable networks selected.');
                      return;
                    }

                    if (!confirm(`Destroy ${destroyableJobs.length} network(s)?`)) return;

                    destroyableJobs.forEach((job: GpuJob) => {
                      removeNetworkMutation.mutate({
                        jobId: job.id,
                        instanceId: job.instanceId || job.id,
                        vpcId: job.vpcId || '',
                      });
                    });
                    setSelectedNetworks(new Set());
                  }}
                  disabled={removeNetworkMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Selected
                </button>
              </div>
            )}
          </div>

          {/* Select All */}
          <div className="flex items-center gap-2 px-4 py-2 mb-2 bg-surface-800/30 rounded-lg">
            <input
              type="checkbox"
              checked={gpuJobs.length > 0 && selectedNetworks.size === gpuJobs.length}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedNetworks(new Set(gpuJobs.map((job: GpuJob) => job.id)));
                } else {
                  setSelectedNetworks(new Set());
                }
              }}
              className="w-4 h-4 rounded border-surface-600 text-gpu-500"
            />
            <span className="text-sm text-surface-400">Select all ({gpuJobs.length} networks)</span>
          </div>

          <div className="space-y-2">
            {(() => {
              const jobsByVpc = gpuJobs.reduce((acc: Record<string, GpuJob[]>, job: GpuJob) => {
                const vpcId = job.vpcId || 'no-vpc';
                if (!acc[vpcId]) acc[vpcId] = [];
                acc[vpcId].push(job);
                return acc;
              }, {} as Record<string, GpuJob[]>);

              const vpcEntries = Object.entries(jobsByVpc);
              const totalPages = Math.ceil(vpcEntries.length / networkPerPage);
              const startIndex = (networkPage - 1) * networkPerPage;
              const paginatedEntries = vpcEntries.slice(startIndex, startIndex + networkPerPage);

              return (
                <>
                  {paginatedEntries.map(([vpcId, jobs]) => {
                    const vpc = networkData.vpcs.find(v => v.vpcId === vpcId);
                    const subnets = networkData.subnets.filter(s => s.vpcId === vpcId);
                    const sgs = networkData.securityGroups.filter(sg => sg.vpcId === vpcId);
                    const isExpanded = selectedInstanceId === `vpc-${vpcId}`;

                    if (vpcId === 'no-vpc') {
                      return jobs.map((job: GpuJob) => (
                        <div key={job.id} className="rounded-lg border border-surface-700/50 overflow-hidden">
                          <div className="flex items-center justify-between px-4 py-3 bg-surface-800/50">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={selectedNetworks.has(job.id)}
                                onChange={(e) => {
                                  const newSelection = new Set(selectedNetworks);
                                  if (e.target.checked) newSelection.add(job.id);
                                  else newSelection.delete(job.id);
                                  setSelectedNetworks(newSelection);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 rounded border-surface-600 text-gpu-500"
                              />
                              <Server className="w-4 h-4 text-surface-500" />
                              <span className="font-medium text-surface-100">
                                {job.instanceId || `GPU Instance ${job.id.slice(0, 8)}`}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-surface-600/50 text-surface-400">
                                No Network
                              </span>
                            </div>
                          </div>
                        </div>
                      ));
                    }

                    return (
                      <div key={vpcId} className="rounded-lg border border-surface-700/50 overflow-hidden">
                        <div
                          className={clsx(
                            'flex items-center justify-between px-4 py-3 cursor-pointer transition-colors',
                            isExpanded ? 'bg-gpu-500/10' : 'bg-surface-800/50 hover:bg-surface-800/80'
                          )}
                          onClick={() => setSelectedInstanceId(isExpanded ? null : `vpc-${vpcId}`)}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={jobs.every((j: GpuJob) => selectedNetworks.has(j.id))}
                              onChange={(e) => {
                                const newSelection = new Set(selectedNetworks);
                                jobs.forEach((job: GpuJob) => {
                                  if (e.target.checked) newSelection.add(job.id);
                                  else newSelection.delete(job.id);
                                });
                                setSelectedNetworks(newSelection);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 rounded border-surface-600 text-gpu-500"
                            />
                            <Globe className="w-4 h-4 text-blue-400" />
                            <span className="font-medium text-surface-100">
                              {jobs.length > 1 ? `${jobs.length} Instances (Shared Network)` : (jobs[0].instanceId || `GPU Instance ${jobs[0].id.slice(0, 8)}`)}
                            </span>
                            <span className={clsx(
                              'text-xs px-2 py-0.5 rounded-full',
                              jobs.length > 1 ? 'bg-blue-500/20 text-blue-300' : 'bg-emerald-500/20 text-emerald-300'
                            )}>
                              {jobs.length > 1 ? 'Shared' : 'Dedicated'}
                            </span>
                          </div>
                          <ChevronRight className={clsx(
                            'w-5 h-5 text-surface-400 transition-transform duration-200',
                            isExpanded && 'rotate-90'
                          )} />
                        </div>

                        {/* Expanded Details */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="border-t border-surface-700/50 bg-surface-900/30"
                            >
                              <div className="p-4 space-y-4">
                                {/* Instances */}
                                <div className="p-3 rounded-lg bg-surface-800/50">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Server className="w-4 h-4 text-gpu-400" />
                                    <span className="text-sm font-medium text-surface-200">Instances</span>
                                  </div>
                                  <div className="space-y-2">
                                    {jobs.map((job: GpuJob) => (
                                      <div key={job.id} className="flex items-center justify-between p-2 rounded bg-surface-900/50">
                                        <div className="flex items-center gap-2">
                                          <span className="font-mono text-xs text-surface-300">
                                            {job.instanceId || job.id.slice(0, 8)}
                                          </span>
                                          <span className="text-xs text-surface-500">{job.instanceType}</span>
                                        </div>
                                        <span className={clsx(
                                          'text-xs px-2 py-0.5 rounded-full',
                                          job.status === 'running' ? 'bg-emerald-500/20 text-emerald-300' :
                                          job.status === 'stopped' ? 'bg-orange-500/20 text-orange-300' :
                                          'bg-surface-600/50 text-surface-400'
                                        )}>
                                          {job.status}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* VPC */}
                                {vpc && (
                                  <div className="p-3 rounded-lg bg-surface-800/50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Layers className="w-4 h-4 text-blue-400" />
                                      <span className="text-sm font-medium text-surface-200">VPC</span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                      <div>
                                        <span className="text-surface-500">ID</span>
                                        <p className="font-mono text-surface-300">{vpc.vpcId}</p>
                                      </div>
                                      <div>
                                        <span className="text-surface-500">CIDR</span>
                                        <p className="font-mono text-surface-300">{vpc.cidrBlock}</p>
                                      </div>
                                      <div>
                                        <span className="text-surface-500">State</span>
                                        <p className={vpc.state === 'available' ? 'text-emerald-400' : 'text-surface-400'}>{vpc.state}</p>
                                      </div>
                                      <div>
                                        <span className="text-surface-500">Tenancy</span>
                                        <p className="text-surface-300">{vpc.instanceTenancy || 'default'}</p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Subnets */}
                                {subnets.length > 0 && (
                                  <div className="p-3 rounded-lg bg-surface-800/50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Layers className="w-4 h-4 text-teal-400" />
                                      <span className="text-sm font-medium text-surface-200">Subnets</span>
                                    </div>
                                    {subnets.map((subnet) => (
                                      <div key={subnet.subnetId} className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-2">
                                        <div>
                                          <span className="text-surface-500">ID</span>
                                          <p className="font-mono text-surface-300">{subnet.subnetId}</p>
                                        </div>
                                        <div>
                                          <span className="text-surface-500">CIDR</span>
                                          <p className="font-mono text-surface-300">{subnet.cidrBlock}</p>
                                        </div>
                                        <div>
                                          <span className="text-surface-500">AZ</span>
                                          <p className="text-surface-300">{subnet.availabilityZone}</p>
                                        </div>
                                        <div>
                                          <span className="text-surface-500">State</span>
                                          <p className={subnet.state === 'available' ? 'text-emerald-400' : 'text-surface-400'}>{subnet.state}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Security Groups */}
                                {sgs.length > 0 && (
                                  <div className="p-3 rounded-lg bg-surface-800/50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Shield className="w-4 h-4 text-orange-400" />
                                      <span className="text-sm font-medium text-surface-200">Security Groups</span>
                                    </div>
                                    {sgs.map((sg) => (
                                      <div key={sg.groupId} className="mb-3">
                                        <div className="grid grid-cols-2 gap-3 text-xs mb-2">
                                          <div>
                                            <span className="text-surface-500">ID</span>
                                            <p className="font-mono text-surface-300">{sg.groupId}</p>
                                          </div>
                                          <div>
                                            <span className="text-surface-500">Name</span>
                                            <p className="font-mono text-surface-300">{sg.groupName}</p>
                                          </div>
                                        </div>

                                        {/* Inbound Rules */}
                                        <div className="mb-2">
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs text-emerald-400 font-medium">Inbound</span>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setAddRuleModal({ groupId: sg.groupId, direction: 'inbound' });
                                              }}
                                              className="text-[10px] text-gpu-400 hover:text-gpu-300 flex items-center gap-1"
                                            >
                                              <Plus className="w-3 h-3" />
                                              Add
                                            </button>
                                          </div>
                                          <div className="space-y-1">
                                            {sg.inboundRules.length === 0 ? (
                                              <span className="text-xs text-surface-500 italic">No rules</span>
                                            ) : (
                                              sg.inboundRules.map((rule, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-xs p-1.5 rounded bg-surface-900/50 group">
                                                  <div className="flex items-center gap-2">
                                                    <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-mono text-[10px]">
                                                      {getProtocolLabel(rule.protocol)}
                                                    </span>
                                                    <span className="text-surface-400">Port {getPortDisplay(rule)}</span>
                                                    <span className="text-surface-500">← {rule.ipRanges[0]?.cidr || 'SG ref'}</span>
                                                  </div>
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleRemoveRule(sg.groupId, 'inbound', rule);
                                                    }}
                                                    className="p-1 rounded hover:bg-red-500/20 text-surface-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                                  >
                                                    <Trash2 className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              ))
                                            )}
                                          </div>
                                        </div>

                                        {/* Outbound Rules */}
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs text-blue-400 font-medium">Outbound</span>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setAddRuleModal({ groupId: sg.groupId, direction: 'outbound' });
                                              }}
                                              className="text-[10px] text-gpu-400 hover:text-gpu-300 flex items-center gap-1"
                                            >
                                              <Plus className="w-3 h-3" />
                                              Add
                                            </button>
                                          </div>
                                          <div className="space-y-1">
                                            {sg.outboundRules.length === 0 ? (
                                              <span className="text-xs text-surface-500 italic">No rules</span>
                                            ) : (
                                              sg.outboundRules.map((rule, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-xs p-1.5 rounded bg-surface-900/50 group">
                                                  <div className="flex items-center gap-2">
                                                    <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-mono text-[10px]">
                                                      {getProtocolLabel(rule.protocol)}
                                                    </span>
                                                    <span className="text-surface-400">Port {getPortDisplay(rule)}</span>
                                                    <span className="text-surface-500">→ {rule.ipRanges[0]?.cidr || 'SG ref'}</span>
                                                  </div>
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleRemoveRule(sg.groupId, 'outbound', rule);
                                                    }}
                                                    className="p-1 rounded hover:bg-red-500/20 text-surface-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                                  >
                                                    <Trash2 className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              ))
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Destroy Actions */}
                                <div className="pt-3 mt-3 border-t border-red-500/20">
                                  <p className="text-xs text-surface-500 mb-2">
                                    Destroying will remove {jobs.length > 1 ? `all ${jobs.length} instances` : 'this instance'} and associated network resources.
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {jobs.map((job: GpuJob) => (
                                      <button
                                        key={job.id}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setNetworkToDestroy({
                                            jobId: job.id,
                                            instanceId: job.instanceId || job.id,
                                            vpcId: vpcId,
                                          });
                                        }}
                                        disabled={removeNetworkMutation.isPending}
                                        className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors text-xs"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                        {job.instanceId || job.id.slice(0, 8)}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 mt-4 border-t border-surface-700">
                      <div className="text-sm text-surface-400">
                        Showing {startIndex + 1}-{Math.min(startIndex + networkPerPage, vpcEntries.length)} of {vpcEntries.length} networks
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setNetworkPage(Math.max(1, networkPage - 1))}
                          disabled={networkPage === 1}
                          className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="w-4 h-4 rotate-180" />
                        </button>
                        <span className="text-sm text-surface-400">Page {networkPage} of {totalPages}</span>
                        <button
                          onClick={() => setNetworkPage(Math.min(totalPages, networkPage + 1))}
                          disabled={networkPage === totalPages}
                          className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Destroy Confirmation Modal */}
      <AnimatePresence>
        {networkToDestroy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setNetworkToDestroy(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-surface-900 border border-red-500/30 rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-400">Destroy Network</h3>
                  <p className="text-sm text-surface-400">This action cannot be undone</p>
                </div>
              </div>

              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                <p className="text-sm text-surface-200 mb-3">
                  This will permanently destroy the following resources:
                </p>
                <ul className="text-sm text-surface-300 space-y-1.5">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    Instance: <code className="text-red-300 font-mono text-xs">{networkToDestroy.instanceId}</code>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    VPC: <code className="text-red-300 font-mono text-xs">{networkToDestroy.vpcId}</code>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    Associated subnets, route tables, and security groups
                  </li>
                </ul>
              </div>

              <div className="flex justify-end gap-3">
                <button onClick={() => setNetworkToDestroy(null)} className="btn-ghost">
                  Cancel
                </button>
                <button
                  onClick={() => removeNetworkMutation.mutate({
                    jobId: networkToDestroy.jobId,
                    instanceId: networkToDestroy.instanceId,
                    vpcId: networkToDestroy.vpcId,
                  })}
                  disabled={removeNetworkMutation.isPending}
                  className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  {removeNetworkMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Destroying...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Destroy Network
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Rule Modal */}
      <AnimatePresence>
        {addRuleModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setAddRuleModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-surface-900 border border-surface-700 rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-surface-100 mb-4">
                Add {addRuleModal.direction === 'inbound' ? 'Inbound' : 'Outbound'} Rule
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="label">Protocol</label>
                  <select
                    value={newRule.protocol}
                    onChange={(e) => setNewRule({ ...newRule, protocol: e.target.value })}
                    className="input"
                  >
                    <option value="tcp">TCP</option>
                    <option value="udp">UDP</option>
                    <option value="icmp">ICMP</option>
                    <option value="all">All Traffic</option>
                  </select>
                </div>
                {newRule.protocol !== 'all' && newRule.protocol !== 'icmp' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">From Port</label>
                      <input
                        type="number"
                        value={newRule.fromPort}
                        onChange={(e) => setNewRule({ ...newRule, fromPort: e.target.value })}
                        placeholder="e.g. 22"
                        className="input"
                        min="0"
                        max="65535"
                      />
                    </div>
                    <div>
                      <label className="label">To Port</label>
                      <input
                        type="number"
                        value={newRule.toPort}
                        onChange={(e) => setNewRule({ ...newRule, toPort: e.target.value })}
                        placeholder="e.g. 22"
                        className="input"
                        min="0"
                        max="65535"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="label">Source/Destination CIDR</label>
                  <input
                    type="text"
                    value={newRule.cidrIp}
                    onChange={(e) => setNewRule({ ...newRule, cidrIp: e.target.value })}
                    placeholder="0.0.0.0/0"
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Description (optional)</label>
                  <input
                    type="text"
                    value={newRule.description}
                    onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                    placeholder="Rule description"
                    className="input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setAddRuleModal(null)} className="btn-ghost">
                  Cancel
                </button>
                <button
                  onClick={handleAddRule}
                  disabled={addRuleMutation.isPending}
                  className="btn-primary"
                >
                  {addRuleMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Add Rule
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
